import React from 'react';

type Body = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  depth: number;
  phase: number;
};

const COLORS = ['253, 203, 18', '255, 85, 0', '255, 255, 255'];

const makeBody = (width: number, height: number, large = false): Body => {
  const depth = Math.random();
  return {
    x: Math.random() * width,
    y: Math.random() * height,
    vx: (Math.random() - 0.5) * (large ? 0.16 : 0.34 + depth * 0.32),
    vy: (Math.random() - 0.5) * (large ? 0.16 : 0.34 + depth * 0.32),
    size: large ? 190 + (1 - depth) * 210 : 0.7 + (1 - depth) * 1.8,
    depth,
    phase: Math.random() * Math.PI * 2,
  };
};

const bounce = (body: Body, width: number, height: number, radius: number) => {
  if (body.x < radius || body.x > width - radius) {
    body.x = Math.max(radius, Math.min(width - radius, body.x));
    body.vx *= -0.82;
    body.vy += (Math.random() - 0.5) * 0.055;
  }
  if (body.y < radius || body.y > height - radius) {
    body.y = Math.max(radius, Math.min(height - radius, body.y));
    body.vy *= -0.82;
    body.vx += (Math.random() - 0.5) * 0.055;
  }
};

/** Decorative, contained particle fluid. The hero remains the input and clipping surface. */
export default function HeroFluid() {
  const rootRef = React.useRef<HTMLDivElement>(null);
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const blobRefs = React.useRef<Array<HTMLDivElement | null>>([]);

  React.useEffect(() => {
    const root = rootRef.current;
    const canvas = canvasRef.current;
    const hero = root?.parentElement;
    const context = canvas?.getContext('2d');
    if (!root || !canvas || !hero || !context) return;

    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    let reduced = motionQuery.matches;
    let visible = true;
    let pageVisible = !document.hidden;
    let frame = 0;
    let width = 1;
    let height = 1;
    let lastTime = 0;
    let dots: Body[] = [];
    let blobs: Body[] = [];

    const draw = (time = 0, advance = false) => {
      const delta = Math.min(2, (time - lastTime) / 16.67 || 1);
      lastTime = time;
      const styles = getComputedStyle(hero);
      const inputX = Number.parseFloat(styles.getPropertyValue('--px')) || 0;
      const inputY = Number.parseFloat(styles.getPropertyValue('--py')) || 0;
      const cursorX = (inputX + 0.5) * width;
      const cursorY = (inputY + 0.5) * height;
      context.clearRect(0, 0, width, height);

      dots.forEach((dot, index) => {
        if (advance) {
          const dx = dot.x - cursorX;
          const dy = dot.y - cursorY;
          const reach = Math.max(130, width * 0.28);
          const influence = Math.max(0, 1 - Math.hypot(dx, dy) / reach);
          dot.vx += (inputX * 0.022 - inputY * 0.012) * influence * (0.5 + dot.depth);
          dot.vy += (inputY * 0.022 + inputX * 0.012) * influence * (0.5 + dot.depth);
          dot.vx += Math.sin(time * 0.00045 + dot.phase) * 0.0022;
          dot.vy += Math.cos(time * 0.00038 + dot.phase) * 0.0022;
          dot.vx *= 0.996;
          dot.vy *= 0.996;
          dot.x += dot.vx * delta;
          dot.y += dot.vy * delta;
          bounce(dot, width, height, dot.size + 1);
        }
        const color = COLORS[index % COLORS.length];
        context.beginPath();
        context.arc(dot.x, dot.y, dot.size, 0, Math.PI * 2);
        context.fillStyle = `rgba(${color}, ${0.18 + (1 - dot.depth) * 0.44})`;
        context.fill();
      });

      blobs.forEach((blob, index) => {
        if (advance) {
          blob.vx += Math.sin(time * 0.00022 + blob.phase) * 0.0012;
          blob.vy += Math.cos(time * 0.00019 + blob.phase) * 0.0012;
          blob.vx += inputX * 0.0035 * (1 - blob.depth * 0.5);
          blob.vy += inputY * 0.0035 * (1 - blob.depth * 0.5);
          blob.vx *= 0.997;
          blob.vy *= 0.997;
          blob.x += blob.vx * delta;
          blob.y += blob.vy * delta;
          bounce(blob, width, height, blob.size * 0.26);
        }
        const element = blobRefs.current[index];
        if (element) {
          element.style.transform = `translate3d(${blob.x - blob.size / 2}px, ${blob.y - blob.size / 2}px, 0)`;
          element.style.width = `${blob.size}px`;
          element.style.height = `${blob.size}px`;
          element.style.opacity = `${0.34 + (1 - blob.depth) * 0.28}`;
          element.style.filter = `blur(${10 + blob.depth * 13}px)`;
        }
      });
    };

    const tick = (time: number) => {
      draw(time, !reduced);
      frame = requestAnimationFrame(tick);
    };
    const syncLoop = () => {
      cancelAnimationFrame(frame);
      frame = 0;
      if (visible && pageVisible && !reduced) frame = requestAnimationFrame(tick);
      else draw(0, false);
    };
    const resize = () => {
      const bounds = root.getBoundingClientRect();
      width = Math.max(1, bounds.width);
      height = Math.max(1, bounds.height);
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(width * ratio);
      canvas.height = Math.round(height * ratio);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      const count = Math.max(60, Math.min(120, Math.round(width / 12)));
      dots = Array.from({ length: width < 640 ? Math.min(72, count) : count }, () => makeBody(width, height));
      blobs = Array.from({ length: 4 }, () => makeBody(width, height, true));
      draw(0, false);
    };
    const observer = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      syncLoop();
    }, { threshold: 0.01 });
    const resizeObserver = new ResizeObserver(resize);
    const onVisibility = () => { pageVisible = !document.hidden; syncLoop(); };
    const onMotion = () => { reduced = motionQuery.matches; syncLoop(); };

    resize();
    observer.observe(hero);
    resizeObserver.observe(root);
    document.addEventListener('visibilitychange', onVisibility);
    motionQuery.addEventListener('change', onMotion);
    syncLoop();
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      resizeObserver.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
      motionQuery.removeEventListener('change', onMotion);
    };
  }, []);

  return (
    <div ref={rootRef} className="hero-fluid" aria-hidden="true">
      <canvas ref={canvasRef} className="hero-fluid__canvas" />
      {[0, 1, 2, 3].map((index) => (
        <div
          key={index}
          ref={(element) => { blobRefs.current[index] = element; }}
          className={`hero-fluid__blob hero-fluid__blob--${index + 1}`}
        />
      ))}
    </div>
  );
}
